import React, { useRef, useEffect, useState } from "react";
import { useTypewriter } from "../hooks/useTypewriter";

/*
 * CopilotSpotlight
 * ─────────────────
 * Full-width dark navy panel with a terminal that types out a Q → A exchange.
 *
 * Mobile / reduced-motion behavior:
 *  - We shorten the script (fewer chars) so it fits & completes fast.
 *  - The useTypewriter hook itself falls back to instant reveal if the user
 *    has prefers-reduced-motion.
 */

const RESPONSE_DESKTOP =
    "Revenue is flat vs last month. Two costs jumped:\n" +
    "  • AWS      +₹42,000  (18% vs 3-mo avg)\n" +
    "  • Rent     +₹35,000  (new Chennai lease starts 1 Jun)\n" +
    "Net: ₹77,000 below trend.\n" +
    "Want me to draft a cost-cut memo?";

const RESPONSE_MOBILE =
    "Two costs jumped:\n" +
    "  • AWS   +₹42,000\n" +
    "  • Rent  +₹35,000\n" +
    "Net: ₹77,000 below trend.";

function useIsMobile() {
    const [m, setM] = useState(() => (typeof window !== "undefined") && window.matchMedia("(max-width: 768px)").matches);
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const mq = window.matchMedia("(max-width: 768px)");
        const onChange = () => setM(mq.matches);
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);
    return m;
}

export default function CopilotSpotlight() {
    const ref = useRef(null);
    const [active, setActive] = useState(false);
    const mobile = useIsMobile();

    // Start typing only when panel is on screen
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") { setActive(true); return undefined; }
        const io = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) { setActive(true); io.disconnect(); }
        }, { threshold: 0.35 });
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const prompt = useTypewriter("why is profit lower this month?", {
        speed: mobile ? 18 : 24, startDelay: 400, active,
    });
    const response = useTypewriter(mobile ? RESPONSE_MOBILE : RESPONSE_DESKTOP, {
        speed: mobile ? 8 : 14, startDelay: mobile ? 1400 : 2100, active,
    });

    return (
        <section className="vy-dark vy-section" id="copilot" ref={ref} data-testid="landing-copilot">
            <div className="vy-container" style={{ position: "relative", zIndex: 1 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.15fr", gap: 48, alignItems: "center" }} className="vy-grid-2">
                    <div className="vy-reveal">
                        <div className="vy-kicker">&gt; copilot.ask --your-books</div>
                        <h2
                            className="vy-serif-i vy-h-section"
                            style={{ fontSize: "3rem", marginTop: 14, color: "var(--vy-paper)", lineHeight: 1.05, letterSpacing: -0.01 }}
                        >
                            Ask your books a question.<br />
                            <span className="vy-serif" style={{ color: "var(--vy-gold)", fontStyle: "normal" }}>Get an answer.</span>
                        </h2>
                        <p style={{ marginTop: 18, color: "rgba(246,242,235,0.7)", fontSize: 15.5, lineHeight: 1.6, maxWidth: 460 }}>
                            No dashboards to build. No filters to configure. Ask in English. Vyaahar reads your ledger, your
                            invoices, and your bank — and answers with numbers you can trust.
                        </p>
                        <ul style={{
                            listStyle: "none", padding: 0, marginTop: 24, display: "grid", gap: 10,
                            fontSize: 13.5, color: "rgba(246,242,235,0.85)", fontFamily: "IBM Plex Mono, monospace",
                        }}>
                            <li>· queries your live books at run-time. no summaries, no snapshots — actual numbers.</li>
                            <li>· cites the transaction it drew each number from — journal, invoice, or bill.</li>
                            <li>· your ledger stays yours. we don&apos;t send it to any provider for training. ever.</li>
                        </ul>
                    </div>

                    <div className="vy-reveal" data-testid="copilot-terminal">
                        <div className="vy-terminal" aria-live="polite">
                            <div>
                                <span className="vy-t-prompt">$ </span>
                                <span className="vy-t-user">{prompt.text}</span>
                                {!prompt.done && <span className="vy-cursor" />}
                            </div>
                            {prompt.done && (
                                <div style={{ marginTop: 12, whiteSpace: "pre-wrap" }}>
                                    {response.text}
                                    {!response.done && <span className="vy-cursor" />}
                                </div>
                            )}
                            {response.done && (
                                <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 10, padding: "4px 10px", background: "rgba(166,138,86,0.16)", color: "#c4a76e", border: "1px solid rgba(166,138,86,0.35)", borderRadius: 2 }}>
                                        yes, draft memo
                                    </span>
                                    <span style={{ fontSize: 10, padding: "4px 10px", background: "rgba(246,242,235,0.06)", color: "rgba(246,242,235,0.6)", border: "1px solid rgba(246,242,235,0.16)", borderRadius: 2 }}>
                                        show journal lines
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
